// hooks/useDeliveryCalculator.ts
import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { CartItem, DeliveryRule } from "../../../types/types";
import { DeliveryType } from "../../../types/enums";
import { selectCurrentCluster } from "../../../../store/slices/currentClusterSlice";
import { selectStoreList } from "../../../../store/slices/storeListSlice";
import { isPointInPolygon } from "../../../../common/utils/functions/isPointInPolygon";

interface UseDeliveryCalculatorProps {
  cartItems: CartItem[];
  deliveryAddress?: {
    latitude: number;
    longitude: number;
  } | null;
  deliveryType: DeliveryType;
}

export const useDeliveryCalculator = ({
  cartItems,
  deliveryAddress,
  deliveryType,
}: UseDeliveryCalculatorProps) => {
  const currentCluster = useSelector(selectCurrentCluster);
  const allStores = useSelector(selectStoreList);

  const [loading, setLoading] = useState(false);
  const [available, setAvailable] = useState(false);
  const [deliveryStoreId, setDeliveryStoreId] = useState<number | null>(null);
  const [deliveryDistance, setDeliveryDistance] = useState<number | null>(null);
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [applicableRules, setApplicableRules] = useState<DeliveryRule[]>([]);
  const [error, setError] = useState<string | null>(null);

  const cartItemsKey = useMemo(() => {
    return cartItems.map((item) => `${item.id}:${item.quantity}`).join("|");
  }, [cartItems]);

  useEffect(() => {
    const calculateDelivery = async () => {
      console.log("[Delivery] ▶ Старт расчёта", {
        deliveryType,
        deliveryAddress,
        hasCluster: !!currentCluster,
        storesCount: allStores.length,
        cartItemsKey,
      });

      setAvailable(false);
      setDeliveryStoreId(null);
      setDeliveryDistance(null);
      setDeliveryFee(0);
      setApplicableRules([]);
      setError(null);

      if (
        deliveryType !== DeliveryType.DELIVERY ||
        !deliveryAddress ||
        !currentCluster
      ) {
        console.log("[Delivery] ⏭ Пропуск: условия не выполнены", {
          isDelivery: deliveryType === DeliveryType.DELIVERY,
          hasAddress: !!deliveryAddress,
          hasCluster: !!currentCluster,
        });
        return;
      }

      setLoading(true);

      try {
        const storesWithDelivery = allStores.filter((s) => s.withDelivery);

        console.log(
          `[Delivery] 🏬 Магазинов с доставкой: ${storesWithDelivery.length} из ${allStores.length}`,
        );

        if (!storesWithDelivery.length) {
          console.warn("[Delivery] ❌ Нет точек с доставкой");
          setError("Нет точек с доставкой");
          return;
        }

        const addressPoint = {
          lat: Number(deliveryAddress.latitude),
          lng: Number(deliveryAddress.longitude),
        };

        console.log("[Delivery] 📍 Адрес клиента:", addressPoint);

        let nearestStore: any = null;
        let minDistance = Infinity;

        for (const store of storesWithDelivery) {
          if (!store.address?.latitude || !store.address?.longitude) {
            console.log(`[Delivery] ⚠ Магазин ${store.id}: нет координат`);
            continue;
          }

          const storeZones = store.deliveryZones || [];

          let inZone = false;
          let matchedZoneIds: number[] = [];

          if (storeZones.length > 0) {
            for (const zone of storeZones) {
              if (isPointInPolygon(addressPoint, zone.polygon)) {
                inZone = true;
                matchedZoneIds.push(zone.clusterDeliveryId);
              }
            }
            console.log(
              `[Delivery] 🗺 Магазин ${store.id}: зон=${storeZones.length}, matched=[${matchedZoneIds.join(", ")}], inZone=${inZone}`,
            );
          } else {
            const distance = calculateDistance(
              Number(store.address.latitude),
              Number(store.address.longitude),
              addressPoint.lat,
              addressPoint.lng,
            );
            if (isNaN(distance)) {
              console.log(`[Delivery] ⚠ Магазин ${store.id}: distance = NaN`);
              continue;
            }

            const distanceMeters = distance * 1000;
            const withinStoreRadius =
              store.deliveryRadius === null ||
              distanceMeters <= Number(store.deliveryRadius);

            if (withinStoreRadius) inZone = true;

            console.log(
              `[Delivery] 📍 Магазин ${store.id}: ${distance.toFixed(3)} км (${distanceMeters.toFixed(0)} м), радиус: ${store.deliveryRadius ?? "∞"}, входит: ${withinStoreRadius}`,
            );
          }

          if (!inZone) continue;

          const distance = calculateDistance(
            Number(store.address.latitude),
            Number(store.address.longitude),
            addressPoint.lat,
            addressPoint.lng,
          );

          if (distance < minDistance) {
            minDistance = distance;
            nearestStore = { ...store, distance, matchedZoneIds };
          }
        }

        if (!nearestStore) {
          console.warn("[Delivery] ❌ Нет магазина в зоне/радиусе");
          setError("Доставка недоступна по этому адресу");
          return;
        }

        console.log(
          `[Delivery] ✅ Ближайший магазин: ${nearestStore.id}, ${nearestStore.distance.toFixed(3)} км, zones=[${(nearestStore.matchedZoneIds || []).join(", ")}]`,
        );

        const itemsTotal = cartItems.reduce(
          (sum, item) => sum + item.price * item.quantity,
          0,
        );

        console.log(`[Delivery] 💰 Сумма корзины: ${itemsTotal}`);

        const store = nearestStore;
        const distanceMeters = store.distance * 1000;
        const allRules = (currentCluster.delivery as DeliveryRule[]) || [];
        const hasZones = (store.deliveryZones || []).length > 0;

        console.log(
          `[Delivery] 🔍 hasZones=${hasZones}, distanceM=${distanceMeters.toFixed(0)}, totalRules=${allRules.length}`,
        );

        let filteredRules: DeliveryRule[];

        if (hasZones) {
          filteredRules = allRules.filter((r) =>
            (store.matchedZoneIds || []).includes(Number((r as any).id)),
          );
          console.log(
            `[Delivery] 🗺 Фильтр по зонам [${(store.matchedZoneIds || []).join(", ")}]:`,
            filteredRules.map((r) => ({
              id: (r as any).id,
              level: r.level,
              value: r.value,
            })),
          );
        } else {
          filteredRules = allRules.filter(
            (r) => Number(r.radius) >= distanceMeters,
          );
          console.log(
            `[Delivery] 📏 Фильтр по радиусу >= ${distanceMeters.toFixed(0)} м:`,
            filteredRules.map((r) => ({
              radius: r.radius,
              level: r.level,
              value: r.value,
            })),
          );
        }

        const applicable = filteredRules
          .filter((r) => Number(r.level) <= itemsTotal)
          .sort((a, b) => Number(a.level) - Number(b.level));

        const allApplicable = filteredRules.sort(
          (a, b) => Number(a.level) - Number(b.level),
        );

        console.log(
          `[Delivery] 📋 Подходящие (level <= ${itemsTotal}):`,
          applicable.map((r) => ({
            level: r.level,
            value: r.value,
            radius: r.radius,
          })),
        );

        let price = 0;
        if (applicable.length) {
          price = Math.min(...applicable.map((r) => Number(r.value)));
          console.log(`[Delivery] 💵 Минимальное value = ${price}`);
        } else {
          console.log("[Delivery] 💵 Нет подходящих правил, цена = 0");
        }

        console.log("[Delivery] 🏁 Итог:", {
          available: true,
          storeId: store.id,
          distanceKm: store.distance,
          fee: price,
        });

        setAvailable(true);
        setDeliveryStoreId(store.id ?? null);
        setDeliveryDistance(store.distance);
        setDeliveryFee(price);
        setApplicableRules(allApplicable);
      } catch (err) {
        console.error("[Delivery] 💥 Ошибка расчёта:", err);
        setError("Ошибка расчета доставки");
      } finally {
        setLoading(false);
        console.log("[Delivery] ◀ Завершено");
      }
    };

    calculateDelivery();
  }, [deliveryType, deliveryAddress, currentCluster, allStores, cartItemsKey]);

  return {
    available,
    deliveryStoreId,
    deliveryDistance,
    deliveryFee,
    applicableRules,
    loading,
    error,
  };
};

const calculateDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};
